import { Check, CircleAlert } from "lucide-react";

import type { OverviewResponse } from "@/shared/api";

import { attention, riskText } from "./attention";
import { rankingVerdict, reconciliationVerdict, type OverviewSources } from "./sources";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

type Tone = "good" | "warning" | "bad" | "muted";

const DOT: Record<Tone, string> = {
  good: "bg-status-positive",
  warning: "bg-status-warning",
  bad: "bg-status-negative",
  muted: "bg-text-muted",
};

function connection(value: string): { readonly value: string; readonly tone: Tone } {
  if (value === "HEALTHY") return { value: "ONLINE", tone: "good" };
  if (value === "DEGRADED") return { value: "DEGRADED", tone: "warning" };
  if (value === "DISCONNECTED") return { value: "OFFLINE", tone: "bad" };
  return { value: "UNKNOWN", tone: "muted" };
}

function HealthRow({
  label,
  value,
  tone,
  testId,
  title,
  detail,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone: Tone;
  readonly testId: string;
  readonly title?: string | undefined;
  readonly detail?: string | undefined;
}): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      title={title}
      className={`min-w-0 py-1.5 ${detail === undefined ? "" : "sm:col-span-2"}`}
    >
      {/* The label never truncates; a long value wraps under itself instead. */}
      <div className="flex min-w-0 items-center justify-between gap-4">
        <span className="shrink-0 text-helper-sm text-text-secondary">{label}</span>
        <span className="flex min-w-0 items-center justify-end gap-2 text-right text-helper-sm tabular-nums text-text-primary">
          <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${DOT[tone]}`} />
          {value}
        </span>
      </div>
      {detail !== undefined && (
        <details className="mt-1 text-helper-sm text-text-secondary">
          <summary className="cursor-pointer">Источник и время наблюдения</summary>
          <p className="mt-1 break-words [overflow-wrap:anywhere]">{detail}</p>
        </details>
      )}
    </div>
  );
}

export function SystemHealth({
  overview,
  sources,
}: {
  readonly overview: OverviewResponse;
  readonly sources?: OverviewSources | undefined;
}): React.JSX.Element {
  const venue = connection(overview.connection);
  const capital = overview.performance.capital;
  const allocation =
    capital == null
      ? { value: "НЕТ ДАННЫХ", tone: "muted" as Tone }
      : capital.over_allocated
        ? { value: "ВХОДЫ ОСТАНОВЛЕНЫ", tone: "warning" as Tone }
        : { value: "ДОСТУПНО", tone: "good" as Tone };
  const riskSafe = overview.portfolio_risk === "SAFE";
  const riskCurrent = overview.connection === "HEALTHY" && !overview.stale;
  // The same list the «Требует проверки» panel below prints, so the two counts agree.
  const issues = attention(overview, sources);
  const market = rankingVerdict(sources?.ranking);
  const reconciliation = reconciliationVerdict(sources?.system);

  const readings: readonly {
    readonly label: string;
    readonly value: string;
    readonly tone: Tone;
    readonly testId: string;
    readonly title?: string;
    readonly detail?: string;
  }[] = [
    { label: "Bybit API", value: venue.value, tone: venue.tone, testId: "health-bybit" },
    {
      label: "Данные счёта",
      value: overview.stale ? "УСТАРЕЛИ" : "АКТУАЛЬНЫ",
      tone: overview.stale ? "warning" : "good",
      testId: "health-account-data",
    },
    {
      label: "Наблюдение ранжирования",
      ...market,
      testId: "health-market-data",
    },
    {
      label: "Портфельный риск",
      value: riskCurrent ? riskText(overview.portfolio_risk).toUpperCase() : `Последнее известное: ${riskText(overview.portfolio_risk)}`,
      tone: riskCurrent ? riskSafe ? "good" : "warning" : "muted",
      testId: "health-portfolio-risk",
    },
    {
      label: "Аллокация стратегии",
      value: allocation.value,
      tone: allocation.tone,
      testId: "health-allocation",
    },
    {
      label: "Сверка",
      ...reconciliation,
      testId: "health-reconciliation",
    },
    {
      label: "Последний тик",
      // A verdict, not an age. The status strip owns the clock, and a second
      // one here would be the same feed timed at a slightly different moment —
      // which reads as two sources that might disagree.
      value: overview.stale ? "ЗАДЕРЖКА" : "В БЮДЖЕТЕ",
      tone: overview.stale ? "warning" : "good",
      testId: "health-last-tick",
      title: `Бюджет свежести: ${String(overview.freshness_budget_seconds)} с. Возраст показан в строке состояния.`,
    },
  ];

  return (
    <section
      data-testid="system-health"
      aria-labelledby="system-health-heading"
      className="flex h-full min-w-0 flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className={PANEL_HEADER}>
        <h2 id="system-health-heading" className={PANEL_TITLE}>
          Работа бота
        </h2>
      </div>

      <div className="grid flex-1 content-between px-3 sm:grid-cols-2 sm:gap-x-6">
        {readings.map((reading) => (
          <HealthRow key={reading.testId} {...reading} />
        ))}
      </div>


      <div className="border-t border-border-subtle px-3 py-2">
        {issues.length > 0 ? (
          <p className="flex items-center gap-1.5 text-helper-sm text-status-warning" data-testid="health-attention">
            <CircleAlert aria-hidden="true" className="size-3.5 shrink-0" />
            Замечаний: {issues.length} — список ниже
          </p>
        ) : market.tone === "good" && reconciliation.tone === "good" && capital != null && capital.running > 0 ? (
          <p className="flex items-center gap-1.5 text-helper-sm text-status-positive" data-testid="health-clear">
            <Check aria-hidden="true" className="size-3.5 shrink-0" />
            Бот работает · по доступным операционным данным
          </p>
        ) : (
          <p className="text-helper-sm text-text-secondary" data-testid="health-unconfirmed">
            Состояние работы не подтверждено полностью
          </p>
        )}
      </div>
    </section>
  );
}
